<script lang="ts" setup>
import { TechDataVehicle } from '@/types';
import TechDataModal from '@/components/tech-data-modal.vue';
import { ref } from 'vue';
import { getDefaultLocaleValue } from '../locales/default';
import { useTracking } from '../composables/useTracking';

const props = defineProps<{
  translations: Record<string, string>;
  vehicle: TechDataVehicle;
  locale: string;
  modelSeries: string;
  validOptions: {
    weights: number[] | null;
    roofs: string[] | null;
    cabins: string[] | null;
    drivetrains: string[] | null;
    batteryCapacities: number[] | null;
    engines: string[] | null;
  };
}>();
const isOpen = ref(false);

// Tracking
const { trackModalButton } = useTracking(props.modelSeries);

// Track modal open button
function handleOpenModal() {
  trackModalButton('all_technical_data', props.vehicle);
  isOpen.value = true;
}
</script>

<template>
  <table class="wbx-table wbx-table--striped">
    <tbody class="wbx-body">
      <tr>
        <th scope="row">
          {{
            props.translations['owvc_tech_data_vehicleDimensions_length'] ||
            getDefaultLocaleValue('vehicleDimensions_length')
          }}
        </th>
        <td>
          {{
            `${vehicle.vehicleDimensions?.length?.value?.toLocaleString(props.locale)} ${
              vehicle.vehicleDimensions?.length?.unit
            }`
          }}
        </td>
      </tr>
      <tr>
        <th scope="row">
          {{
            props.translations['owvc_tech_data_vehicleDimensions_height'] ||
            getDefaultLocaleValue('vehicleDimensions_height')
          }}
        </th>
        <td>
          {{
            `${vehicle.vehicleDimensions?.height?.value?.toLocaleString(props.locale)} ${
              vehicle.vehicleDimensions?.height?.unit
            }`
          }}
        </td>
      </tr>
      <tr v-if="vehicle.loadingSpaceDimensions?.lengthMax?.value">
        <th scope="row">
          {{
            props.translations[
              'owvc_tech_data_loadingSpaceDimensions_lengthMax'
            ] || getDefaultLocaleValue('loadingSpaceDimensions_lengthMax')
          }}
        </th>
        <td>
          {{
            `${vehicle.loadingSpaceDimensions?.lengthMax?.value?.toLocaleString(props.locale)} ${
              vehicle.loadingSpaceDimensions?.lengthMax?.unit
            }`
          }}
        </td>
      </tr>
      <tr v-if="vehicle.loadingSpaceDimensions?.heightMax?.value">
        <th scope="row">
          {{
            props.translations[
              'owvc_tech_data_loadingSpaceDimensions_heightMax'
            ] || getDefaultLocaleValue('loadingSpaceDimensions_heightMax')
          }}
        </th>
        <td>
          {{
            `${vehicle.loadingSpaceDimensions?.heightMax?.value?.toLocaleString(props.locale)} ${
              vehicle.loadingSpaceDimensions?.heightMax?.unit
            }`
          }}
        </td>
      </tr>
      <tr>
        <th scope="row">
          {{
            props.translations['owvc_tech_data_weights_payloadMax'] ||
            getDefaultLocaleValue('weights_payloadMax')
          }}
        </th>
        <td>
          {{
            `${vehicle.weights?.payloadMax?.value?.toLocaleString(props.locale)} ${vehicle.weights?.payloadMax?.unit}`
          }}
        </td>
      </tr>
    </tbody>
  </table>
  <div class="wbx-button-wrapper">
    <button
      class="wbx-button wbx-button--secondary wbx-button--large"
      data-test-id="owvans-tech-data-modal-open-button"
      style="flex: 0 1 auto"
      @click="handleOpenModal"
    >
      {{
        translations['owvc_tech_data_all_technical_data'] ||
        getDefaultLocaleValue('allTechnicalData')
      }}
    </button>
  </div>
  <tech-data-modal
    :translations="translations"
    :locale="locale"
    :vehicle="vehicle"
    :validOptions="validOptions"
    :isOpen="isOpen"
    :modelSeries="modelSeries"
    @close="isOpen = false"
  />
</template>
<style lang="scss" scoped>
@import '@workbench/core/dist/scss/utility.scss';
.wbx-table {
  width: 100%;
  margin-top: 32px;
  margin-bottom: 32px;
}
th {
  padding-top: 24px;
  padding-bottom: 24px;
  width: 100%;
}

td {
  white-space: nowrap;
}

.wbx-button-wrapper {
  display: flex;
  justify-content: center;
  margin-bottom: 32px;
}
</style>
